import React, { useEffect, useState } from "react";
import { Badge, Modal, Button, Drawer, List, Spin } from "antd";
import { BellOutlined, HistoryOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { useUpdates, Update } from "../hooks/useUpdates";
import { useFormatDate } from "../hooks/useFormatDate";
import { OPEN_UPDATES_DRAWER, type OpenUpdatesDrawerDetail } from "../constants/updatesDrawer";

// Fallback data in case the database table doesn't exist yet
const fallbackUpdates: Update[] = [
  {
    id: 1,
    title: "Producten & Boekingen: Nu samen op één plek!",
    description: `
      <h2>🔄 Producten & Boekingen: Nu samen op één plek!</h2>

      <p>We hebben een nieuwe functie toegevoegd aan de "Mijn Producten" pagina! Je kunt nu:</p>

      <h3>Nieuwe Functies:</h3>
      <ul>
          <li>📋 <strong>Boekingen per Product:</strong> 
              <ul>
                  <li>Zie direct alle boekingen gekoppeld aan elk product</li>
                  <li>Bekijk boeking ID's en statussen zonder naar een andere pagina te hoeven gaan</li>
              </ul>
          </li>
          <li>🔍 <strong>Filter op Status:</strong>
              <ul>
                  <li>Gebruik het selectievakje "Toon alleen actieve boekingen" bovenaan de tabel</li>
                  <li>Schakel eenvoudig tussen actieve en alle boekingen</li>
              </ul>
          </li>
          <li>🔄 <strong>Automatisch Sorteren:</strong>
              <ul>
                  <li>Boekingen worden automatisch gesorteerd op datum (nieuwste eerst)</li>
                  <li>Houd eenvoudig je meest recente boekingen in het oog</li>
              </ul>
          </li>
      </ul>

      <h3>Actieve Boekingen Omvatten:</h3>
      <ul>
          <li>Concept</li>
          <li>Ingediend</li>
          <li>Goedgekeurd</li>
          <li>Afgewezen</li>
          <li>Wachten op Goederen</li>
          <li>Goederen Ontvangen</li>
          <li>In Transit</li>
          <li>Douane</li>
          <li>Onderweg naar Bestemming</li>
      </ul>

      <p><em>Tip: Houd je muis boven het selectievakje "Toon alleen actieve boekingen" voor een overzicht van alle statussen die als "actief" worden beschouwd.</em></p>
    `,
    created_at: new Date().toISOString(),
  },
  {
    id: 2,
    title: "Kolombeheer voor Boekingen",
    description: `
      <h2>🔄 Kolombeheer Update</h2>

      <p>We hebben de kolomweergave in de boekingentabel verbeterd! Je kunt nu:</p>

      <h3>Nieuwe Functies:</h3>
      <ul>
          <li>📊 <strong>Kolommen Aanpassen:</strong> 
              <ul>
                  <li>Klik op het tandwiel-icoon naast de tabel</li>
                  <li>Vink kolommen aan of uit om ze te tonen of verbergen</li>
              </ul>
          </li>
          <li>↕️ <strong>Kolommen Herschikken:</strong>
              <ul>
                  <li>Sleep kolommen omhoog of omlaag om de volgorde aan te passen</li>
                  <li>Gebruik het hamburger-menu (≡) links van elke kolom om te slepen</li>
              </ul>
          </li>
          <li>🔄 <strong>Sorteren:</strong>
              <ul>
                  <li>Klik op een kolomkop om te sorteren</li>
                  <li>Klik nogmaals om de sorteervolgorde om te draaien</li>
              </ul>
          </li>
      </ul>

      <h3>Extra Functies:</h3>
      <ul>
          <li>✨ "Selecteer Alles" optie om alle kolommen in één keer te tonen</li>
          <li>🔄 "Standaard Herstellen" knop om terug te keren naar de oorspronkelijke instellingen</li>
          <li>💾 Je aangepaste kolomvoorkeuren worden automatisch opgeslagen</li>
      </ul>

      <p><em>Tip: De Boeking ID en Status kolommen zijn altijd zichtbaar voor een beter overzicht.</em></p>
    `,
    created_at: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString(), // 1 week ago
  },
];

// Styled components
const NotificationIconWrapper = styled.div`
  position: relative;
  cursor: pointer;
  padding: 8px;
  display: flex;
  align-items: center;
`;

const UpdateModal = styled(Modal)`
  .ant-modal-body {
    max-height: 70vh;
    overflow-y: auto;
  }
`;

const StyledDrawer = styled(Drawer)`
  .ant-drawer-body {
    padding: 0;
  }
`;

const UpdateItem = styled.div`
  padding: 20px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  h3 {
    margin-top: 0;
    margin-bottom: 8px;
    color: #1890ff;
  }

  .content {
    margin-bottom: 10px;
  }

  .date {
    color: #888;
    font-size: 12px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
`;

// Main component
export const UpdateNotification: React.FC = () => {
  const { t } = useTranslation();
  const formatDate = useFormatDate();
  const [hasNewUpdates, setHasNewUpdates] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [updateIdToScroll, setUpdateIdToScroll] = useState<number | null>(null);
  const [updateToShowInModal, setUpdateToShowInModal] = useState<Update | null>(null);
  const { updates, loading, error } = useUpdates({ limit: 999 });

  // Use database updates when available, otherwise use fallback data
  const allUpdates = updates && updates.length > 0 ? updates : fallbackUpdates;

  // Get the latest update for modal display
  const latestUpdate = allUpdates[0];

  // Store the ID of the latest update to compare with what the user has seen
  const latestUpdateId = latestUpdate?.id;

  useEffect(() => {
    if (latestUpdateId) {
      checkForNewUpdates();
    }
  }, [latestUpdateId]);

  useEffect(() => {
    const onOpen = (e: Event) => {
      const d = (e as CustomEvent<OpenUpdatesDrawerDetail>).detail;
      if (d?.update) {
        setUpdateToShowInModal(d.update);
        setIsModalVisible(true);
      } else {
        setUpdateIdToScroll(d?.updateId ?? null);
        setIsDrawerVisible(true);
      }
    };
    window.addEventListener(OPEN_UPDATES_DRAWER, onOpen);
    return () => window.removeEventListener(OPEN_UPDATES_DRAWER, onOpen);
  }, []);

  useEffect(() => {
    if (!isDrawerVisible || updateIdToScroll == null) return;
    const id = updateIdToScroll;
    setUpdateIdToScroll(null);
    const tid = setTimeout(() => {
      document.querySelector(`[data-update-id="${id}"]`)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }, 100);
    return () => clearTimeout(tid);
  }, [isDrawerVisible, updateIdToScroll]);

  // Check if there are updates newer than the last viewed
  const checkForNewUpdates = () => {
    if (!latestUpdateId) return;

    // Get the current user ID (in a real app, this would come from auth context)
    const userId = getCurrentUserId();

    // Get the ID of the last update viewed by this user
    const lastViewedUpdateId = localStorage.getItem(
      `lastViewedUpdateId_${userId}`
    );

    // If the user hasn't viewed updates yet or the latest update is newer than the last viewed
    const hasNew =
      !lastViewedUpdateId || parseInt(lastViewedUpdateId) < latestUpdateId;

    setHasNewUpdates(hasNew);
  };

  // Mark updates as viewed
  const markUpdatesAsViewed = () => {
    if (!latestUpdateId) return;

    const userId = getCurrentUserId();
    // Store the latest update ID instead of a timestamp
    localStorage.setItem(
      `lastViewedUpdateId_${userId}`,
      latestUpdateId.toString()
    );
    setHasNewUpdates(false);
  };

  // Get current user ID (simplified for demonstration, should use actual auth system)
  const getCurrentUserId = (): string => {
    // In a real app, get this from your auth context/state
    return localStorage.getItem("currentUserId") || "default-user";
  };

  // Handle notification click
  const handleNotificationClick = () => {
    if (hasNewUpdates) {
      // Show modal with latest update if there are new updates
      setIsModalVisible(true);
      markUpdatesAsViewed();
    } else {
      // Show drawer with all updates if there are no new updates
      setIsDrawerVisible(true);
    }
  };

  // Handle viewing all updates from the modal
  const handleViewAllUpdates = () => {
    setUpdateToShowInModal(null);
    setIsModalVisible(false);
    setIsDrawerVisible(true);
  };

  // If still loading updates, return just the icon with a loading state
  if (loading && updates.length === 0) {
    return (
      <>
        <NotificationIconWrapper onClick={handleNotificationClick}>
          <Badge dot={false}>
            <BellOutlined style={{ fontSize: "20px", opacity: 0.6 }} />
          </Badge>
        </NotificationIconWrapper>
      </>
    );
  }

  return (
    <>
      <NotificationIconWrapper onClick={handleNotificationClick}>
        <Badge dot={hasNewUpdates}>
          <BellOutlined style={{ fontSize: "20px" }} />
        </Badge>
      </NotificationIconWrapper>

      {/* Modal for Latest Update (shown when there are new updates) */}
      <UpdateModal
        title={t("common.updates", "What's New")}
        open={isModalVisible}
        onCancel={() => { setUpdateToShowInModal(null); setIsModalVisible(false); }}
        footer={[
          <Button key="view-all" onClick={handleViewAllUpdates} type="primary">
            {t("common.viewAllUpdates", "View All Updates")}
          </Button>,
          <Button key="close" onClick={() => { setUpdateToShowInModal(null); setIsModalVisible(false); }}>
            {t("common.close", "Close")}
          </Button>,
        ]}
        width={800}
      >
        {loading && !updateToShowInModal ? (
          <LoadingContainer>
            <Spin size="large" />
          </LoadingContainer>
        ) : error && !updateToShowInModal ? (
          <div style={{ color: "red" }}>{error}</div>
        ) : (updateToShowInModal || latestUpdate) ? (
          (() => {
            const u = updateToShowInModal || latestUpdate!;
            return (
              <div key={u.id}>
                <h3>{u.title}</h3>
                <div dangerouslySetInnerHTML={{ __html: u.description }} />
                <div style={{ color: "#888", fontSize: "12px", marginTop: "10px" }}>
                  {formatDate(u.created_at)}
                </div>
              </div>
            );
          })()
        ) : (
          <div>{t("common.noUpdatesAvailable", "No updates available")}</div>
        )}
      </UpdateModal>

      {/* Drawer for All Updates (shown when clicking without new updates) */}
      <StyledDrawer
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <HistoryOutlined style={{ marginRight: "8px" }} />
            {t("common.updateHistory", "Update History")}
          </div>
        }
        placement="right"
        onClose={() => setIsDrawerVisible(false)}
        open={isDrawerVisible}
        width={480}
      >
        {loading ? (
          <LoadingContainer>
            <Spin size="large" />
          </LoadingContainer>
        ) : error ? (
          <div style={{ padding: 20, color: "red" }}>{error}</div>
        ) : allUpdates && allUpdates.length > 0 ? (
          <List
            dataSource={allUpdates}
            renderItem={(update) => (
              <UpdateItem key={update.id} data-update-id={update.id}>
                <h3>{update.title}</h3>
                <div
                  className="content"
                  dangerouslySetInnerHTML={{ __html: update.description }}
                />
                <div className="date">
                  {formatDate(update.created_at)}
                </div>
              </UpdateItem>
            )}
          />
        ) : (
          <div style={{ padding: 20 }}>
            {t("common.noUpdatesAvailable", "No updates available")}
          </div>
        )}
      </StyledDrawer>
    </>
  );
};

export default UpdateNotification;
